<template>
    <div
        v-if="!$route.meta.requiresAuth || $route.meta.webPortal"
        :class="[
            'mr-web-ui',
            {
                'web-portal': $route.meta.webPortal,
                'patient-dash': !$route.meta.webPortal
            }
        ]"
    >
        <NotifyMsg />
        <router-view :key="$route.path" />
    </div>

    <div
        v-else
        :id="$route.name"
        class="mr-web-ui client-dash ui relaxed grid container mr"
    >
        <div class="outer-column column">
            <Navigation />
            <NotifyMsg />
            <!-- Track route path changes instead of component changes -->
            <router-view :key="$route.path" />
        </div>
    </div>

    <VueQueryDevtools />
</template>

<script>
import Analytics from '@serv/Analytics'
import Auth from '@serv/Auth'
import ConfigManager from '@config/ConfigManager'
import Locale from '@serv/Locale.js'
import Logging from '@serv/Logging'
import LoginService from '@serv/LoginService'
import { mapGetters } from 'vuex'
import moment from 'moment'
import Navigation from '@comp/MrNavigation.vue'
import NotifyMsg from '@comp/NotifyMsg.vue'
import NotifyService from '@serv/NotifyService.js'
import Redirect from '@serv/Redirect'
import RegistrationService from '@serv/RegistrationService'
import Request from '@serv/Request'
import ResourceService from '@serv/ResourceService'
import Storage from '@serv/Storage'
import UnauthenticatedAuth from '@serv/Auth/UnauthenticatedAuth'
import { useImpersonationStore } from '@/stores/impersonation'
import { useNetwork } from '@vueuse/core'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import Whoami from '@serv/Whoami'

export default {
    name: 'App',
    setup() {
        const { isOnline } = useNetwork()
        const impersonationStore = useImpersonationStore()

        return { isOnline, impersonationStore }
    },
    components: {
        Navigation,
        NotifyMsg,
        VueQueryDevtools
    },
    inject: ['$bus', '$config', '$mp'],
    data() {
        return {
            sessionTimer: null,
            offlineNotification: null
        }
    },
    computed: {
        ...mapGetters(['owner', 'user'])
    },
    methods: {
        // Get the number of min of inactivity after which we will forcibly logout.
        // Specified on the owner keyValues, or 15m fallback.
        getMaxInactivityMins() {
            if (ConfigManager.logoutInactivity === false) {
                return 99999
            }

            const ownerMins = (((this.owner || {}).keyValues || {}).dash || {}).logoutInactivityMins

            return ownerMins || 15
        },
        addActivityEventListeners() {
            window.addEventListener('click', this.resetTimer)
            window.addEventListener('keyup', this.resetTimer)
            window.addEventListener('keydown', this.resetTimer)
            window.addEventListener('keypress', this.resetTimer)
            window.addEventListener('scroll', this.resetTimer)
            // Touch events for mobile/tablet devices
            window.addEventListener('touchstart', this.resetTimer)
        },
        removeActivityEventListeners() {
            window.removeEventListener('click', this.resetTimer)
            window.removeEventListener('keyup', this.resetTimer)
            window.removeEventListener('keydown', this.resetTimer)
            window.removeEventListener('keypress', this.resetTimer)
            window.removeEventListener('scroll', this.resetTimer)
            // Touch events for mobile/tablet devices
            window.removeEventListener('touchstart', this.resetTimer)
        },
        resetTimer() {
            if (ConfigManager.isMockingServer) {
                return
            }
            clearTimeout(this.sessionTimer)

            Auth.lastActivityMoment = moment()
            this.sessionTimer = setTimeout(() => {
                this.onInactiveUser()
            }, this.getMaxInactivityMins() * 60 * 1000)
        },
        onInactiveUser() {
            // If logged in, then log out
            if (this.user) {
                if (this.user.isPatient) {
                    // Ensure we go back to the correct patient start page
                    RegistrationService.logoutAndRedirect()
                } else {
                    LoginService.logout().then(() => {
                        Redirect.gotoName('Logout')
                    })
                }
            }
        },
        setDocumentTitle() {
            if (this.$route.name === 'Login') {
                document.title = this.$config.branding.dashboardLoginPageName || this.$config.branding.dashboardName
            } else {
                document.title = this.$config.branding.dashboardName
            }
        },
        showLogoutNotification() {
            const { messageFunction, messageText } = Storage.get('logoutNotification') || {}
            if (messageFunction && messageText) {
                NotifyService[messageFunction](messageText, 1000)
                Storage.remove('logoutNotification')
            }
        }
    },
    beforeMount() {
        const _this = this
        Request.setImpersonationUser(this.impersonationStore.impersonatedUser)
        this.addActivityEventListeners()
        const handleSession = () => {
            // Handle logout on inactivity
            if (Auth.lastActivityMoment == undefined) {
                Auth.lastActivityMoment = moment()
            }
            let lastActivityInMinutes
            if (ConfigManager.isMockingServer) {
                lastActivityInMinutes = 1
            } else {
                lastActivityInMinutes = moment
                    .duration(moment().diff(Auth.lastActivityMoment))
                    .asMinutes()
            }

            // Handle inactivity
            const maxInactivityMins = this.getMaxInactivityMins()
            if (lastActivityInMinutes < maxInactivityMins) {
                // Initialisation, e.g. on page reload
                if (Auth.hasSession()) {
                    if (this.impersonationStore.impersonatedUser) {
                        return this.$router.isReady().then(() => {
                            if (this.$route.name === 'Login') {
                                this.impersonationStore.$reset()
                            }
                            Whoami.getActiveUser(this.impersonationStore.impersonatedUser)
                                .then(user => {
                                    ResourceService.requestAll(user)
                                })
                                .catch(error => Logging.log(error))
                        })
                    }
                    Whoami.getActiveUser(this.impersonationStore.impersonatedUser)
                        .then(user => {
                            ResourceService.requestAll(user).then(() => {
                                // IMPORTANT: Reset timer after owner data is loaded
                                // This ensures getMaxInactivityMins() uses the actual owner config
                                // (e.g. 24h) instead of the 15min fallback value
                                _this.resetTimer()
                            })
                        })
                        .catch(error => Logging.log(error))
                } else if (UnauthenticatedAuth.hasSession()) {
                    UnauthenticatedAuth.doAuth()
                        .catch(() => {
                            LoginService.logout()
                        })
                    _this.resetTimer()
                } else {
                    this.$router.isReady().then(() => {
                        if (this.$route.meta?.webPortal) {
                            Redirect.gotoName('Patient')
                        }
                    })
                }
            } else {
                Logging.warn(
                    `Exceeded ${maxInactivityMins} mins of inactivity, logging out.`
                )
                _this.onInactiveUser()
            }
        }

        if (this.$config.preLogin && !Auth.hasSession()) {
            Auth.doSilentAuth(this.$route.query.redirect)
                .then(() => handleSession())
                .catch(error => {
                    Logging.error(`CorinAuth: doSilentAuth failed. Error: ${error}`)
                    _this.resetTimer()
                })
        } else {
            handleSession()
        }
    },
    created() {
        if (this.$bus) {
            this.$bus.$off()
        }

        // workaround to add class for body. Used for changing background for web-portal pages.
        if (this.$route.meta?.webPortal) {
            document.body.classList.add('body-web-portal')
        }

        this.setDocumentTitle()
    },
    mounted() {
        this.showLogoutNotification()
        Analytics.init(this.$mp)
    },
    beforeUnmount() {
        this.removeActivityEventListeners()
        clearTimeout(this.sessionTimer)
        this.$bus.$all.clear()
    },
    watch: {
        $route(to, from) {
            this.showLogoutNotification()
            this.setDocumentTitle()

            Analytics.sendEvent('Page Viewed', {
                page: to.name,
                pageUrl: to.fullPath,
                from: from.name,
                fromUrl: from.fullPath
            })
        },
        isOnline(isOnline) {
            if (!isOnline) {
                this.offlineNotification = NotifyService.error(
                    Locale.getLanguageItem('networkOffline')
                )
                NotifyService.sticky(this.offlineNotification)

                return
            }

            NotifyService.expireNow(this.offlineNotification)
            NotifyService.info(
                Locale.getLanguageItem('networkOnline')
            )
        }
    }
}
</script>

<style lang="scss">
@import './styles/common';

@font-face {
    font-family: Omnes;
    src: url('/fonts/OmnesReg.ttf') format('truetype');
    ascent-override: 80%;
    descent-override: 20%;
    line-gap-override: 0%;
}

@font-face {
    font-family: Omnes Medium;
    src: url('/fonts/OmnesMed.ttf') format('truetype');
    ascent-override: 80%;
    descent-override: 20%;
    line-gap-override: 0%;
}
</style>
